import React, {
  forwardRef,
  Fragment,
  KeyboardEventHandler,
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from 'lib/utils';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Label } from 'components/ui/label';
import { IconClear, IconCloseEye, IconEye } from '@app/icons';
import { setMask } from '@app/helpers';

function unSetMask(value: string): string {
  return value.replace(/\D/g, '');
}

export interface TextFieldProps {
  label: string;
  value: string;
  placeholder?: string;
  onChange?: (value: string, name?: string) => void;
  helperText?: string | null;
  error?: boolean;
  mask?: string;
  secureTextEntry?: boolean;
  name?: string;
  editable?: boolean;
  onClick?: () => void;
  onClickClear?: () => void;
  endIcons?: ReactNode[];
  onSendMessage?: () => void;
  required?: boolean;
  variant?: 'default' | 'modern';
  className?: string;
  multiline?: boolean;
  autoComplete?: 'on' | 'off';
  labelClassName?: string;
}

type Ref = any;

function TextField(props: TextFieldProps, ref: Ref) {
  const {
    label,
    value,
    onChange,
    placeholder,
    helperText = '',
    error = false,
    mask,
    secureTextEntry = false,
    name,
    editable = true,
    endIcons,
    onClick,
    onSendMessage,
    required = false,
    variant = 'default',
    className = '',
    multiline = false,
    autoComplete = 'on',
    labelClassName,
  } = props;

  const [showHiddenText, setShowHiddenText] = useState<boolean>(false);
  const inputElement = useRef<HTMLInputElement>(null);
  const textAreaElement = useRef<HTMLTextAreaElement>(null);

  const valueText = useMemo<string>(() => {
    return mask ? setMask(value || '', mask.trim()) : value || '';
  }, [mask, value]);

  const onClickClear = useCallback(() => {
    if (!!props.onClickClear) {
      props.onClickClear();
    }
    onChange?.('', name);
  }, [onChange, props, name]);

  const onKeyDown: KeyboardEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        if (!!onSendMessage) {
          onSendMessage();
        }
      }
    },
    [onSendMessage]
  );

  const onChangeInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (!editable || !onChange) {
        return;
      }
      const { value: inputValue } = e.target;
      if (!mask) {
        onChange(inputValue, name as string);
        return;
      }
      if (inputValue.length <= mask.trim().length) {
        onChange(unSetMask(inputValue), name as string);
      }
    },
    [editable, mask, name, onChange]
  );

  const onClickInput = useCallback(
    (e: React.MouseEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (!!onClick) {
        e.preventDefault();
        e.stopPropagation();
        if (multiline) {
          if (!!textAreaElement.current) textAreaElement.current.blur();
        } else if (!!inputElement.current) {
          inputElement.current.blur();
        }
        onClick();
      }
    },
    [multiline, onClick]
  );

  const onFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (autoComplete === 'off') e.target.removeAttribute('readonly');
    },
    [autoComplete]
  );

  const onChangeType = useCallback(() => {
    setShowHiddenText((prevShowHidden) => !prevShowHidden);
  }, []);

  if (variant === 'modern') {
    return (
      <div className={cn('flex flex-col gap-1', className)}>
        <Label
          className={cn('text-xs text-muted-foreground mb-1', labelClassName)}
        >
          {label}
          {required ? ' *' : ''}
        </Label>
        <div className="relative">
          <Input
            ref={ref}
            type={secureTextEntry && !showHiddenText ? 'password' : 'text'}
            className={cn('pr-10', error && 'border-red-500')}
            onChange={onChangeInput}
            value={valueText}
            disabled={!editable}
            placeholder={placeholder}
            onClick={onClickInput}
            onKeyDown={onKeyDown}
            autoComplete={autoComplete}
            onFocus={onFocus}
            readOnly={autoComplete === 'off'}
          />
          {secureTextEntry && (
            <span
              className="absolute right-8 top-1/2 -translate-y-1/2 cursor-pointer"
              onClick={onChangeType}
            >
              {showHiddenText ? <IconCloseEye /> : <IconEye />}
            </span>
          )}
          {!secureTextEntry && !!valueText && (
            <span
              className="absolute right-8 top-1/2 -translate-y-1/2 cursor-pointer"
              onClick={onClickClear}
            >
              <IconClear />
            </span>
          )}
          {endIcons &&
            endIcons.map((endIcon, endIconIndex) => (
              <Fragment key={endIconIndex.toString()}>{endIcon}</Fragment>
            ))}
        </div>
        {!!helperText && (
          <div
            className={cn(
              'text-xs mt-1',
              error ? 'text-red-500' : 'text-muted-foreground'
            )}
          >
            {helperText}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn('flex flex-col gap-1', className, {
        'opacity-60 pointer-events-none': !editable,
      })}
    >
      <Label
        className={cn('text-xs text-muted-foreground mb-1', labelClassName)}
      >
        {label}
        {required ? ' *' : ''}
      </Label>
      <div
        className={cn('relative flex items-center', multiline && 'items-start')}
      >
        {multiline ? (
          <Textarea
            ref={textAreaElement}
            className={cn('pr-10', error && 'border-red-500')}
            onChange={onChangeInput}
            value={valueText}
            disabled={!editable}
            placeholder={placeholder}
            onClick={onClickInput}
            onKeyDown={onKeyDown}
            autoComplete={autoComplete}
            onFocus={onFocus}
            readOnly={autoComplete === 'off'}
          />
        ) : (
          <Input
            ref={inputElement}
            type={secureTextEntry && !showHiddenText ? 'password' : 'text'}
            className={cn('pr-10', error && 'border-red-500')}
            onChange={onChangeInput}
            value={valueText}
            disabled={!editable}
            placeholder={placeholder}
            onClick={onClickInput}
            onKeyDown={onKeyDown}
            autoComplete={autoComplete}
            onFocus={onFocus}
            readOnly={autoComplete === 'off'}
          />
        )}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2">
          {secureTextEntry && (
            <span className="cursor-pointer" onClick={onChangeType}>
              {showHiddenText ? <IconCloseEye /> : <IconEye />}
            </span>
          )}
          {!secureTextEntry && !!valueText && !!onChange && (
            <span className="cursor-pointer" onClick={onClickClear}>
              <IconClear />
            </span>
          )}
          {endIcons &&
            endIcons.map((endIcon, endIconIndex) => (
              <Fragment key={endIconIndex.toString()}>{endIcon}</Fragment>
            ))}
        </div>
      </div>
      {!!helperText && (
        <div
          className={cn(
            'text-xs mt-1',
            error ? 'text-red-500' : 'text-muted-foreground'
          )}
        >
          {helperText}
        </div>
      )}
    </div>
  );
}

export default memo(forwardRef<Ref, TextFieldProps>(TextField));
